import { forwardRef, MouseEvent, ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { Box, BoxProps } from '@bonusly/design-system';

import { useProfilePath } from '../hooks/use-profile-path';

interface ProfileLinkProps extends BoxProps {
	userId: string;
	children: ReactNode;
	onClick?: (e: MouseEvent) => void;
	style?: React.CSSProperties;
	className?: string;
}

/**
 * A link component that navigates to a user's profile.
 *
 * When company is on go-forward tier (Free, Team, Organization): Uses React Router for SPA navigation
 * When company is on legacy tier: Uses regular anchor tag for monolith navigation
 *
 * Uses forwardRef to properly work with PopoverTrigger and other components that need refs.
 */
export const ProfileLink = forwardRef<HTMLAnchorElement, ProfileLinkProps>(({ userId, children, onClick, style, className, ...rest }, ref) => {
	const { getProfilePath } = useProfilePath();
	const path = getProfilePath(userId);

	return (
		<Box
			ref={ref}
			as={RouterLink}
			to={path}
			onClick={onClick}
			textDecoration="none"
			_hover={{ textDecoration: 'none' }}
			style={style}
			className={className}
			{...rest}
		>
			{children}
		</Box>
	);
});

ProfileLink.displayName = 'ProfileLink';

/**
 * Hook that returns profile link props for use with Chakra components.
 */
export const useProfileLinkProps = (userId: string) => {
	const { getProfilePath } = useProfilePath();

	return {
		isRouterLink: true as const,
		as: RouterLink,
		to: getProfilePath(userId),
	};
};
